
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../hooks/useData';
import { LoadingSpinner, ConfirmModal, StandardPageLayout } from '../components/Common';
import {
    Box, Button, Card, CardContent, Typography, IconButton, Chip
} from '@mui/material';
import { Add, Edit, Delete, Science } from '@mui/icons-material';
import { apiFetch } from '../api';
import { useNotification } from '../context/NotificationContext';
import { InputAdornment, MenuItem, Stack, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const TenantMLStudioListPage = ({ token }) => {
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    const { data: flows, isLoading, refresh } = useData('/flows', token);
    const mlFlows = flows?.filter(f => f.flowType === 'ml_flow') || [];

    const handleCreate = async () => {
        const name = prompt("Enter Model Name:");
        if (!name) return;
        try {
            const res = await apiFetch('/flows', token, {
                method: 'POST',
                body: JSON.stringify({
                    Name: name,
                    Description: "Created via ML Studio",
                    FlowType: "ml_flow",
                    GraphJson: JSON.stringify({ nodes: [], edges: [] })
                })
            });
            navigate(`/analytics/ml-studio/${res.id}`);
        } catch (e) {
            addNotification(e.message, "error");
        }
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('updated_desc');

    const handleDelete = (id) => {
        setConfirmAction(() => async () => {
            try {
                await apiFetch(`/flows/${id}`, token, { method: 'DELETE' });
                refresh();
                addNotification("ML Model deleted successfully");
            } catch (e) {
                addNotification(e.message, "error");
            }
            setConfirmOpen(false);
        });
        setConfirmOpen(true);
    };

    const headerActions = (
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            New ML Model
        </Button>
    );
    const filteredFlows = useMemo(() => {
        return mlFlows.filter((flow) =>
            (flow.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (flow.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [mlFlows, searchTerm]);
    const sortedFlows = useMemo(() => {
        const list = [...filteredFlows];
        list.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.updated_at || a.createdAt || 0).getTime();
            const dateB = new Date(b.updatedAt || b.updated_at || b.createdAt || 0).getTime();
            if (sortOption === 'updated_desc') return dateB - dateA;
            if (sortOption === 'updated_asc') return dateA - dateB;
            if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
            return 0;
        });
        return list;
    }, [filteredFlows, sortOption]);
    const filterControls = (
        <Stack direction="row" spacing={2} alignItems="center">
            <TextField
                size="small"
                placeholder="Search ML models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ width: 220, bgcolor: 'background.paper' }}
            />
            <TextField
                size="small"
                select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                sx={{ width: 170, bgcolor: 'background.paper' }}
            >
                <MenuItem value="updated_desc">Recently Updated</MenuItem>
                <MenuItem value="updated_asc">Oldest Updated</MenuItem>
                <MenuItem value="name_asc">Name A-Z</MenuItem>
                <MenuItem value="name_desc">Name Z-A</MenuItem>
            </TextField>
        </Stack>
    );

    return (
        <StandardPageLayout
            title="ML Studio"
            description="Build, train, and deploy your own ML models using the Flow Engine."
            headerActions={headerActions}
            filterControls={filterControls}
        >
            {isLoading && <LoadingSpinner />}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                {!isLoading && sortedFlows.map(flow => (
                    <Card key={flow.id} sx={{ borderRadius: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                                    <Science fontSize="small" />
                                </div>
                                <Typography variant="h6" noWrap>{flow.name}</Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ minHeight: '40px', mb: 2 }}>
                                {flow.description || "No description provided."}
                            </Typography>

                            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                                <Chip
                                    label={flow.isActive ? "Active" : "Inactive"}
                                    size="small"
                                    color={flow.isActive ? "success" : "default"}
                                    variant="outlined"
                                />
                                <Box display="flex" gap={1}>
                                    <Button size="small" variant="outlined" startIcon={<Edit />}
                                        onClick={() => navigate(`/analytics/ml-studio/${flow.id}`)}>
                                        Open
                                    </Button>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(flow.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}

                {!isLoading && sortedFlows.length === 0 && (
                    <div className="col-span-3 text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <Science sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">No ML Models Found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Create your first machine learning pipeline to get started.
                        </Typography>
                        <Button variant="outlined" onClick={handleCreate}>Create ML Model</Button>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmAction}
                title="Delete ML Model"
                message="Are you sure you want to delete this ML Model? This action cannot be undone."
            />
        </StandardPageLayout>
    );
};

export default TenantMLStudioListPage;
